import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const TABS = ['notifications', 'profil', 'securite', 'compte'];
const TAB_LABELS = {
  notifications: '🔔 Notifications',
  profil:        '👤 Profil',
  securite:      '🔒 Sécurité',
  compte:        '⚙️ Compte',
};

export default function SettingsPage({ onNavigate }) {
  const [activeTab, setActiveTab]     = useState('notifications');
  const [user, setUser]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);

  // Notifications
  const [notifs, setNotifs] = useState({
    email_reservations: true,
    email_messages:     true,
    email_promotions:   false,
    email_rappels:      true,
  });

  // Profil
  const [fullName, setFullName]   = useState('');
  const [phone, setPhone]         = useState('');
  const [langue, setLangue]       = useState('fr');

  // Sécurité
  const [pwdNew, setPwdNew]         = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdError, setPwdError]     = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);

  // Compte
  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadData = useCallback(async (uid) => {
    try {
      const { data: prof } = await supabase.from('host_profiles').select('*').eq('id', uid).single();
      if (prof) {
        setFullName(prof.full_name || '');
        setPhone(prof.phone || '');
        setLangue(prof.langue || 'fr');
        if (prof.notifications) setNotifs(n => ({ ...n, ...prof.notifications }));
      }
    } catch (e) {
      console.error('Settings load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { setUser(session.user); loadData(session.user.id); }
      else { setLoading(false); onNavigate('auth'); }
    });
  }, [loadData, onNavigate]);

  const showSaved = () => { setSaved(true); setTimeout(() => setSaved(false), 2500); };

  const saveNotifs = async () => {
    setSaving(true);
    await supabase.from('host_profiles').update({ notifications: notifs }).eq('id', user.id);
    setSaving(false);
    showSaved();
  };

  const saveProfil = async () => {
    setSaving(true);
    await supabase.from('host_profiles').update({ full_name: fullName, phone, langue }).eq('id', user.id);
    setSaving(false);
    showSaved();
  };

  const savePassword = async () => {
    setPwdError('');
    if (pwdNew !== pwdConfirm) { setPwdError('Les mots de passe ne correspondent pas.'); return; }
    if (pwdNew.length < 8) { setPwdError('Le mot de passe doit faire au moins 8 caractères.'); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pwdNew });
    setSaving(false);
    if (error) { setPwdError(error.message); } else { setPwdSuccess(true); setPwdNew(''); setPwdConfirm(''); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid var(--border)',
    borderRadius: 10, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none',
    color: 'var(--ink)', background: 'var(--white)', boxSizing: 'border-box',
  };

  const Toggle = ({ checked, onChange, label, desc }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ flex: 1, paddingRight: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!checked)} style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: checked ? 'var(--green)' : 'var(--border)', position: 'relative',
        transition: 'background 0.2s', flexShrink: 0,
      }}>
        <span style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Chargement...</div>
    </div>
  );

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2.5rem 2rem' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <button onClick={() => onNavigate('home')} style={{ fontSize: 13, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', padding: 0, marginBottom: 12 }}>← Retour</button>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, letterSpacing: '-0.5px', marginBottom: 4 }}>Paramètres</h1>
          <p style={{ fontSize: 14, color: 'var(--ink-3)' }}>{user?.email}</p>
        </div>

        {/* Toast saved */}
        {saved && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--ink)', color: '#fff', padding: '10px 20px', borderRadius: 24, fontSize: 13, fontWeight: 500, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            ✓ Modifications enregistrées
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>

          {/* Sidebar tabs */}
          <div style={{ width: isMobile ? '100%' : 200, flexShrink: 0 }}>
            <div style={{ background: 'var(--white)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
              {TABS.map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  style={{ width: '100%', padding: '13px 16px', background: activeTab === tab ? 'var(--green-light)' : 'none', border: 'none', textAlign: 'left', fontSize: 14, color: activeTab === tab ? 'var(--green-mid)' : 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)', fontWeight: activeTab === tab ? 500 : 400, borderBottom: '1px solid var(--border)' }}>
                  {TAB_LABELS[tab]}
                </button>
              ))}
            </div>
          </div>

          {/* Contenu */}
          <div style={{ flex: 1 }}>
            <div style={{ background: 'var(--white)', borderRadius: 14, border: '1px solid var(--border)', padding: '24px' }}>

              {/* ── NOTIFICATIONS ── */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 4 }}>Notifications</h2>
                  <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>Choisissez les emails que vous souhaitez recevoir.</p>

                  <Toggle
                    checked={notifs.email_reservations}
                    onChange={v => setNotifs(p => ({ ...p, email_reservations: v }))}
                    label="Nouvelles réservations"
                    desc="Recevez un email à chaque nouvelle demande de réservation."
                  />
                  <Toggle
                    checked={notifs.email_messages}
                    onChange={v => setNotifs(p => ({ ...p, email_messages: v }))}
                    label="Messages voyageurs"
                    desc="Soyez notifié quand un voyageur vous envoie un message."
                  />
                  <Toggle
                    checked={notifs.email_rappels}
                    onChange={v => setNotifs(p => ({ ...p, email_rappels: v }))}
                    label="Rappels et check-in"
                    desc="Rappels 48h avant chaque arrivée voyageur."
                  />
                  <Toggle
                    checked={notifs.email_promotions}
                    onChange={v => setNotifs(p => ({ ...p, email_promotions: v }))}
                    label="Actualités Havenly"
                    desc="Conseils pour optimiser vos annonces, nouveautés plateforme."
                  />

                  <button onClick={saveNotifs} disabled={saving}
                    style={{ marginTop: 24, padding: '11px 28px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              )}

              {/* ── PROFIL ── */}
              {activeTab === 'profil' && (
                <div>
                  <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 4 }}>Profil</h2>
                  <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>Ces informations sont visibles par les voyageurs.</p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Nom complet</label>
                      <input value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} placeholder="Prénom Nom" />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Téléphone</label>
                      <input value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} placeholder="+33 6 12 34 56 78" />
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>Jamais partagé publiquement</div>
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Langue préférée</label>
                      <select value={langue} onChange={e => setLangue(e.target.value)} style={{ ...inputStyle }}>
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="de">Deutsch</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Email</label>
                      <input value={user?.email || ''} disabled style={{ ...inputStyle, background: 'var(--surface)', color: 'var(--ink-3)' }} />
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>L'email ne peut pas être modifié ici</div>
                    </div>
                  </div>

                  <button onClick={saveProfil} disabled={saving}
                    style={{ marginTop: 24, padding: '11px 28px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              )}

              {/* ── SÉCURITÉ ── */}
              {activeTab === 'securite' && (
                <div>
                  <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 4 }}>Sécurité</h2>
                  <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>Gérez votre mot de passe et la sécurité de votre compte.</p>

                  {pwdSuccess ? (
                    <div style={{ background: '#E8F5F0', borderRadius: 10, padding: '16px 20px', fontSize: 14, color: '#0F6E56', fontWeight: 500 }}>
                      ✓ Mot de passe modifié avec succès !
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div>
                        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Nouveau mot de passe</label>
                        <input type="password" value={pwdNew} onChange={e => setPwdNew(e.target.value)} style={inputStyle} placeholder="Au moins 8 caractères" />
                      </div>
                      <div>
                        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}>Confirmer le mot de passe</label>
                        <input type="password" value={pwdConfirm} onChange={e => setPwdConfirm(e.target.value)} style={inputStyle} placeholder="Répétez le mot de passe" />
                      </div>
                      {pwdError && <div style={{ fontSize: 13, color: '#A32D2D', background: '#FCEBEB', padding: '10px 14px', borderRadius: 8 }}>{pwdError}</div>}
                      <button onClick={savePassword} disabled={saving || !pwdNew || !pwdConfirm}
                        style={{ padding: '11px 28px', background: pwdNew && pwdConfirm ? 'var(--ink)' : 'var(--border)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: pwdNew && pwdConfirm ? 'pointer' : 'default', fontFamily: 'var(--sans)', width: 'fit-content' }}>
                        {saving ? 'Modification...' : 'Modifier le mot de passe'}
                      </button>
                    </div>
                  )}

                  <div style={{ marginTop: 28, padding: '16px 20px', background: 'var(--surface)', borderRadius: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sessions actives</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12 }}>Vous êtes connecté sur cet appareil.</div>
                    <button onClick={async () => { await supabase.auth.signOut(); onNavigate('home'); }}
                      style={{ fontSize: 13, color: '#A32D2D', background: 'none', border: '1px solid #F09595', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                      Se déconnecter partout
                    </button>
                  </div>
                </div>
              )}

              {/* ── COMPTE ── */}
              {activeTab === 'compte' && (
                <div>
                  <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 4 }}>Compte</h2>
                  <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 24 }}>Gérez les paramètres avancés de votre compte.</p>

                  <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '20px', marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Télécharger mes données</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12, lineHeight: 1.6 }}>
                      Recevez une copie de vos données personnelles (réservations, messages, profil) par email.
                    </div>
                    <button onClick={() => alert('Votre demande a été enregistrée. Vous recevrez vos données sous 72h.')}
                      style={{ fontSize: 13, color: 'var(--ink)', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                      Demander mes données
                    </button>
                  </div>

                  <div style={{ background: '#FCEBEB', borderRadius: 12, padding: '20px', border: '1px solid #F09595' }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#A32D2D', marginBottom: 4 }}>Supprimer mon compte</div>
                    <div style={{ fontSize: 13, color: '#A32D2D', marginBottom: 16, lineHeight: 1.6, opacity: 0.8 }}>
                      Cette action est irréversible. Toutes vos données, annonces et réservations seront supprimées définitivement.
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 13, color: '#A32D2D', display: 'block', marginBottom: 6 }}>Tapez <strong>SUPPRIMER</strong> pour confirmer</label>
                      <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                        style={{ ...inputStyle, border: '1px solid #F09595', maxWidth: 240 }} placeholder="SUPPRIMER" />
                    </div>
                    <button
                      disabled={deleteConfirm !== 'SUPPRIMER'}
                      onClick={async () => {
                        await supabase.from('host_profiles').delete().eq('id', user.id);
                        await supabase.auth.admin?.deleteUser(user.id);
                        await supabase.auth.signOut();
                        onNavigate('home');
                      }}
                      style={{ fontSize: 13, color: '#fff', background: deleteConfirm === 'SUPPRIMER' ? '#A32D2D' : '#ccc', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: deleteConfirm === 'SUPPRIMER' ? 'pointer' : 'default', fontFamily: 'var(--sans)', fontWeight: 500 }}>
                      Supprimer définitivement
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}